import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { GraduationCap, ArrowLeft, Loader2, BookOpen, ChevronRight, Home, Lock, ShoppingCart, ExternalLink, Globe, Download, Upload, Target, RefreshCw, Eye } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import FloatingBackButton from "@/components/FloatingBackButton";
import PremiumPopup from "@/components/PremiumPopup";

// App version for cache control
const NOTES_VERSION = "3.2.0";

// Configure axios to prevent caching
const notesApi = axios.create();
notesApi.defaults.headers.common['Cache-Control'] = 'no-cache, no-store, must-revalidate';
notesApi.defaults.headers.common['Pragma'] = 'no-cache';

// Add timestamp to all requests to bust cache
notesApi.interceptors.request.use((config) => {
  config.params = { ...config.params, _v: NOTES_VERSION, _t: Date.now() };
  return config;
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a586d417-0f0e-433d-918a-53924203f4fd/artifacts/y1n33gwl_Image%2016-12-25%20at%2010.38%E2%80%AFAM.jpeg";

// Force clear all caches
const clearAllCaches = async () => {
  try {
    // Clear browser caches
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map(name => caches.delete(name)));
    }
    // Clear localStorage cache items
    localStorage.removeItem('notes_state');
    // Unregister service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
    }
    return true;
  } catch (e) {
    console.error('Cache clear error:', e);
    return false;
  }
};

const pgtSubjects = [
  { value: "hindi", label: "Hindi" },
  { value: "english", label: "English" },
  { value: "mathematics", label: "Mathematics" },
  { value: "physics", label: "Physics" },
  { value: "chemistry", label: "Chemistry" },
  { value: "biology", label: "Biology" },
  { value: "commerce", label: "Commerce" },
  { value: "economics", label: "Economics" },
  { value: "geography", label: "Geography" },
  { value: "history", label: "History" },
  { value: "computer_science", label: "Computer Science" },
  { value: "music", label: "Music" },
  { value: "art", label: "Art" }
];

const tgtSubjects = [
  { value: "hindi", label: "Hindi" },
  { value: "english", label: "English" },
  { value: "mathematics", label: "Mathematics" },
  { value: "science", label: "Science" },
  { value: "social_science", label: "Social Science" },
  { value: "computer_science", label: "Computer Science" },
  { value: "physical_education", label: "Physical Education" },
  { value: "music", label: "Music" },
  { value: "art", label: "Art" }
];

const nonTeachingSubjects = [
  { value: "hostel_warden", label: "Hostel Warden" },
  { value: "library_science", label: "Library Science" }
];

// Topics will be fetched from API dynamically

// Render notes with download option only - no preview
// Export NotesRenderer for use in LMS
export function NotesRenderer({ content, images, noteId, topic, subject, examType, language }) {
  // Build PDF URL for download
  const pdfUrl = `${API}/notes/pdf?exam_type=${encodeURIComponent(examType)}&subject=${encodeURIComponent(subject)}&topic=${encodeURIComponent(topic)}&language=${encodeURIComponent(language)}`;

  const handleDownload = () => {
    // Open PDF in new window AND trigger download
    // Using both methods to ensure download works across browsers
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.target = '_blank';
    link.download = `${topic.replace(/[^a-zA-Z0-9]/g, '_')}_notes.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="notes-renderer">
      {/* Download PDF Button Only */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-6 flex flex-col items-center justify-center text-center">
        <div className="text-white mb-4">
          <h3 className="font-bold text-2xl mb-2">📥 Your Notes are Ready!</h3>
          <p className="text-white/80">{topic}</p>
        </div>
        <Button 
          onClick={handleDownload}
          size="lg"
          className="bg-white text-green-600 hover:bg-gray-100 font-semibold px-8 py-3 text-lg"
        >
          <Download className="w-5 h-5 mr-2" /> Download PDF
        </Button>
      </div>
    </div>
  );
}

const REAL_FACTS = [
  "EMRS was established in 1998 under the Ministry of Tribal Affairs to provide quality education to tribal students.",
  "India's first university, Nalanda, was established in the 5th century CE and attracted scholars from across Asia.",
  "The Indian education system is the world's largest with over 1.5 million schools and 50,000+ higher education institutions.",
  "CBSE was formed in 1962 and currently affiliates over 28,000 schools across India and abroad.",
  "The Right to Education Act 2009 made education a fundamental right for children aged 6-14 in India.",
  "KVS runs over 1,250 Kendriya Vidyalayas across India, making it one of the world's largest chains of schools.",
  "NVS manages 661 Jawahar Navodaya Vidyalayas, providing free residential education to talented rural children.",
  "The National Education Policy 2020 aims to transform India's education system with a 5+3+3+4 structure.",
  "India has the world's largest youth population — over 600 million people under the age of 25.",
  "Sanskrit is considered the mother of many Indian languages and was the medium of instruction in ancient gurukuls.",
  "The concept of zero was invented in India by Aryabhata, revolutionizing mathematics worldwide.",
  "EMRS Tier-2 descriptive paper carries 60 marks with 15 questions — that's 4 marks per question!",
  "Chanakya's Arthashastra, written around 300 BCE, is one of the earliest treatises on economics and statecraft.",
  "India's space program ISRO has launched missions to Moon (Chandrayaan) and Mars (Mangalyaan) at record low costs.",
  "The Dewey Decimal Classification system, used in libraries worldwide, was invented by Melvil Dewey in 1876."
];

function NotesLoadingScreen({ topic, subject, isGenerating = false, elapsedFromServer = 0, partialNotes = null, partsCompleted = 0, language = "english" }) {
  // 4 minutes = 240 seconds for new notes generation (Claude Sonnet is faster)
  const estimatedTime = isGenerating ? 240 : 30;
  const [countdown, setCountdown] = useState(Math.max(0, estimatedTime - elapsedFromServer));
  const [currentFact, setCurrentFact] = useState(0);
  const [eraseProgress, setEraseProgress] = useState((elapsedFromServer / estimatedTime) * 100);
  const [elapsed, setElapsed] = useState(elapsedFromServer);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => prev > 0 ? prev - 1 : 0);
      setElapsed(prev => prev + 1);
      setEraseProgress(prev => Math.min(prev + (100 / estimatedTime), 100));
    }, 1000);
    return () => clearInterval(timer);
  }, [estimatedTime]);

  useEffect(() => {
    const factTimer = setInterval(() => {
      setCurrentFact(prev => (prev + 1) % REAL_FACTS.length);
    }, 5000);
    return () => clearInterval(factTimer);
  }, []);

  const formatTime = (s) => {
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  // If we have partial notes, show the progressive view
  if (partialNotes && partsCompleted > 0) {
    return (
      <div className="max-w-4xl mx-auto">
        {/* Progress Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-t-2xl p-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Loader2 className="w-5 h-5 text-white animate-spin" />
            <span className="text-white font-semibold">
              {language === "hindi" ? "नोट्स तैयार हो रहे हैं..." : "Notes are being prepared..."}
            </span>
          </div>
          {/* Parts Progress Bar */}
          <div className="flex items-center justify-center gap-2 mt-3">
            {[1, 2, 3, 4, 5].map((partNum) => (
              <div key={partNum} className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  partNum <= partsCompleted 
                    ? 'bg-green-500 text-white' 
                    : partNum === partsCompleted + 1 
                      ? 'bg-white text-amber-600 animate-pulse' 
                      : 'bg-white/30 text-white/60'
                }`}>
                  {partNum <= partsCompleted ? '✓' : partNum}
                </div>
                <span className="text-xs text-white/80 mt-1">
                  {partNum <= partsCompleted ? (language === "hindi" ? "पूर्ण" : "Done") : 
                   partNum === partsCompleted + 1 ? (language === "hindi" ? "लोड हो रहा..." : "Loading...") : ""}
                </span>
              </div>
            ))}
          </div>
          <p className="text-white/80 text-sm mt-3">
            {language === "hindi" 
              ? `भाग ${partsCompleted}/5 पूर्ण • बाकी भाग लोड हो रहे हैं...`
              : `Part ${partsCompleted}/5 complete • Loading remaining parts...`}
          </p>
        </div>
        
        {/* Notes Content - Show what we have so far */}
        <div className="bg-white rounded-b-2xl shadow-xl p-6 md:p-10 border border-t-0 border-gray-200">
          <NotesRenderer content={partialNotes} images={[]} noteId="" topic={topic} subject={subject} examType="" language={language} />
          
          {/* Loading indicator for remaining parts */}
          {partsCompleted < 5 && (
            <div className="mt-8 p-6 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 text-center">
              <Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto mb-3" />
              <p className="text-amber-800 font-medium">
                {language === "hindi" 
                  ? `भाग ${partsCompleted + 1} लोड हो रहा है... (~30 सेकंड)`
                  : `Loading Part ${partsCompleted + 1}... (~30 seconds)`}
              </p>
              <p className="text-amber-600 text-sm mt-1">
                {language === "hindi" 
                  ? "आप ऊपर के भाग पढ़ना शुरू कर सकते हैं"
                  : "You can start reading the content above"}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default loading screen (no partial content yet) - NEW EMAIL NOTIFICATION MESSAGE
  return (
    <div className="text-center py-12">
      {/* Email Icon */}
      <div className="relative w-32 h-32 mx-auto mb-8">
        <div className="w-full h-full bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-xl">
          <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        {/* Animated ring */}
        <div className="absolute inset-0 rounded-full border-4 border-amber-300 animate-ping opacity-30"></div>
      </div>

      {/* Main Message */}
      <div className="mb-8 max-w-md mx-auto">
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl p-6 shadow-lg mb-6">
          <h2 className="text-2xl font-bold mb-3">
            📚 NOTES ARE BEING PREPARED
          </h2>
          <p className="text-white/90 text-lg mb-4">
            We will send the <span className="font-bold">PDF</span> to your email in <span className="font-bold">10 minutes</span>
          </p>
          <div className="bg-white/20 rounded-lg p-3">
            <p className="text-white font-medium">
              "{topic}"
            </p>
          </div>
        </div>

        {/* Hindi message */}
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-5">
          <p className="text-amber-800 font-bold text-lg mb-2">
            📧 नोट्स तैयार हो रहे हैं
          </p>
          <p className="text-amber-700">
            हम आपको <span className="font-bold">PDF</span> <span className="font-bold">10 मिनट</span> में आपकी ईमेल पर भेज देंगे
          </p>
        </div>
      </div>

      {/* What to do next */}
      <div className="max-w-lg mx-auto bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
        <p className="text-gray-500 text-sm mb-3">आप इस बीच क्या कर सकते हैं:</p>
        <div className="flex flex-col gap-2 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-green-600">✓</span>
            <span>अपना ईमेल चेक करें | Check your email for PDF</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">✓</span>
            <span>अन्य विषय पढ़ें | Read other topics</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-amber-600">✓</span>
            <span>Practice tests दें | Take practice tests</span>
          </div>
        </div>
      </div>

      {/* Real Facts */}
      <div className="max-w-lg mx-auto bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-200 shadow-sm mt-6">
        <p className="text-xs text-amber-500 font-semibold uppercase tracking-wider mb-2">Did you know?</p>
        <p className="text-sm text-gray-700 leading-relaxed transition-opacity duration-500">
          {REAL_FACTS[currentFact]}
        </p>
      </div>
    </div>
  );
}

export default function Notes({ user, subjectSelection, onLogin }) {
  // If subjectSelection is provided and has a specific subject, start from step 3 (topic selection)
  // If subject is 'all' (all-subjects exam like KVS PRT), start from step 2 (subject browsing)
  const isAllSubjects = subjectSelection?.subject === 'all';
  const [step, setStep] = useState(() => {
    if (!subjectSelection) return 1;
    if (isAllSubjects) return 2; // KVS PRT: browse subjects freely
    return 3; // TGT/PGT: locked to specific subject
  });
  const [examType, setExamType] = useState(() => subjectSelection?.category || "");
  const [subject, setSubject] = useState(() => (subjectSelection?.subject && subjectSelection.subject !== 'all') ? subjectSelection.subject : "");
  const [topic, setTopic] = useState("");
  const [language, setLanguage] = useState("");
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState(null);
  const [images, setImages] = useState([]);
  const [noteId, setNoteId] = useState("");
  const [loadingMessage, setLoadingMessage] = useState("");
  const [generationStatus, setGenerationStatus] = useState(null); // 'ready', 'generating'
  const [partialNotes, setPartialNotes] = useState(null); // For progressive loading
  const [partsCompleted, setPartsCompleted] = useState(0); // Track parts completed
  const [activeGenerations, setActiveGenerations] = useState([]); // Track all user's active generations
  const [isLmsPreview, setIsLmsPreview] = useState(() => {
    // Check URL params immediately on mount
    const params = new URLSearchParams(window.location.search);
    return params.get('preview') === 'true';
  });
  const pollingRef = useRef(null);
  const activeJobsPollingRef = useRef(null);
  const initialLoadDone = useRef(false);
  
  // Rewrite functionality states
  const [showRewriteModal, setShowRewriteModal] = useState(false);
  const [rewriteCode, setRewriteCode] = useState("");
  const [rewriteLoading, setRewriteLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const REWRITE_SECRET_CODE = "2232";

  // Premium popup state
  const [showPremiumPopup, setShowPremiumPopup] = useState(false);

  const isPaid = user.plan !== "free" || user.questions_limit > 0;

  // Dynamic topics from API
  const [currentTopics, setCurrentTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);

  // Topic progress tracking
  const [coveredTopics, setCoveredTopics] = useState(new Set());

  // Dynamic exams & subjects from LMS API
  const [lmsExams, setLmsExams] = useState([]);
  const [lmsSubjects, setLmsSubjects] = useState([]);
  const [loadingExams, setLoadingExams] = useState(false);
  const [loadingSubjects, setLoadingSubjects] = useState(false);

  // Fetch topics from database API
  const fetchTopicsFromAPI = useCallback(async (examTypeVal, subjectVal) => {
    if (!examTypeVal || !subjectVal) {
      setCurrentTopics([]);
      return;
    }
    
    setLoadingTopics(true);
    try {
      // Use the same endpoint as LMS Manager
      const res = await axios.get(`${API}/notes/lms/syllabus/exams/${examTypeVal}/subjects/${subjectVal}/topics`);
      const topics = res.data.topics || [];
      // Filter out placeholders and format topics
      const formattedTopics = topics
        .filter(t => t && t !== '__placeholder__')
        .map(t => ({
          value: t,
          label: t
        }));
      setCurrentTopics(formattedTopics);
    } catch (err) {
      console.error("Failed to fetch topics:", err);
      setCurrentTopics([]);
    } finally {
      setLoadingTopics(false);
    }
  }, []);

  // Fetch exams from LMS API on mount
  useEffect(() => {
    setLoadingExams(true);
    axios.get(`${API}/notes/lms/syllabus/exams`)
      .then(res => {
        const exams = (res.data.exams || []).filter(e => e.subjects_count > 0);
        setLmsExams(exams);
      })
      .catch(() => setLmsExams([]))
      .finally(() => setLoadingExams(false));
  }, []);

  // Fetch subjects when exam changes
  useEffect(() => {
    if (!examType) { setLmsSubjects([]); return; }
    setLoadingSubjects(true);
    axios.get(`${API}/notes/lms/syllabus/exams/${examType}/subjects`)
      .then(res => setLmsSubjects(res.data.subjects || []))
      .catch(() => setLmsSubjects([]))
      .finally(() => setLoadingSubjects(false));
  }, [examType]);

  // Fetch topics when subject changes
  useEffect(() => {
    if (examType && subject) {
      fetchTopicsFromAPI(examType, subject);
      // Fetch coverage for this exam+subject (notes section)
      if (user?.id) {
        axios.get(`${API}/progress?user_id=${encodeURIComponent(user.id)}&exam_type=${encodeURIComponent(examType)}&subject=${encodeURIComponent(subject)}&section=notes`)
          .then(r => setCoveredTopics(new Set(r.data.covered || [])))
          .catch(() => {});
      }
    } else {
      setCurrentTopics([]);
    }
  }, [examType, subject, fetchTopicsFromAPI]);

  // Handle URL params for LMS preview mode - runs immediately
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const examParam = params.get('exam');
    const subjectParam = params.get('subject');
    const topicParam = params.get('topic');
    const langParam = params.get('lang');
    const previewParam = params.get('preview');
    
    if (previewParam === 'true' && examParam && subjectParam && topicParam && langParam) {
      // Mark initialLoadDone so localStorage restore doesn't interfere
      initialLoadDone.current = true;
      
      // LMS Preview mode - directly load the note
      setIsLmsPreview(true);
      setExamType(examParam);
      setSubject(subjectParam);
      setTopic(topicParam);
      setLanguage(langParam);
      setStep(5); // Go to loading step
      setLoading(true);
      setLoadingMessage("Loading preview...");
      
      // Fetch the note directly
      const fetchPreviewNote = async () => {
        try {
          const res = await notesApi.get(`${API}/notes/get`, {
            params: { exam_type: examParam, subject: subjectParam, topic: topicParam, language: langParam, user_id: user.id }
          });
          
          if (res.data.status === "ready" && res.data.notes) {
            setNotes(res.data.notes);
            setImages(res.data.images || []);
            setNoteId(res.data.note_id || "");
            setGenerationStatus("ready");
            setStep(6);
          } else {
            toast.error("Note not found or still generating");
            setStep(1);
            setIsLmsPreview(false);
          }
        } catch (e) {
          toast.error("Failed to load note");
          setStep(1);
          setIsLmsPreview(false);
        } finally {
          setLoading(false);
        }
      };
      
      fetchPreviewNote();
    }
  }, []); // Run only on mount

  // Poll for user's active note generations
  useEffect(() => {
    if (isLmsPreview) return; // Skip polling in LMS preview mode
    
    const fetchActiveGenerations = async () => {
      try {
        const res = await notesApi.get(`${API}/notes/user-active-jobs`, {
          params: { user_id: user.id }
        });
        if (res.data.active_jobs) {
          setActiveGenerations(res.data.active_jobs);
        }
      } catch (e) {
        // Ignore errors
      }
    };

    // Fetch on mount and every 15 seconds
    fetchActiveGenerations();
    activeJobsPollingRef.current = setInterval(fetchActiveGenerations, 15000);

    return () => {
      if (activeJobsPollingRef.current) {
        clearInterval(activeJobsPollingRef.current);
      }
    };
  }, [user.id, isLmsPreview]);


  // currentTopics is now managed by state and fetched from API

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (step === 6 && notes && examType && subject && topic && language && !isLmsPreview) {
      const savedState = { examType, subject, topic, language, step: 6 };
      localStorage.setItem('notes_state', JSON.stringify(savedState));
    }
  }, [step, notes, examType, subject, topic, language, isLmsPreview]);

  // Restore state from localStorage on mount (skip if LMS preview)
  useEffect(() => {
    if (initialLoadDone.current || isLmsPreview) return;
    initialLoadDone.current = true;

    const saved = localStorage.getItem('notes_state');
    if (saved) {
      try {
        const state = JSON.parse(saved);
        // Only restore if the saved subject matches current subject selection
        // This prevents loading old topics when subject is changed
        if (state.examType && state.subject && state.topic && state.language) {
          if (subjectSelection && 
              (state.examType !== subjectSelection.category || state.subject !== subjectSelection.subject)) {
            // Subject has changed - clear old state
            localStorage.removeItem('notes_state');
            return;
          }
          
          // IMPORTANT: Always use the CURRENT medium from subjectSelection
          // This fixes the bug where changing medium on Dashboard was ignored
          const currentLang = subjectSelection?.language || state.language;
          
          // If language changed, clear old state and don't restore
          if (currentLang !== state.language) {
            localStorage.removeItem('notes_state');
            // Don't restore - let user pick topic fresh with new language
            return;
          }
          
          // Restore state
          setExamType(state.examType);
          setSubject(state.subject);
          setTopic(state.topic);
          setLanguage(currentLang);
          
          // Load the notes directly
          setLoading(true);
          setGenerationStatus("checking");
          setStep(5);
          
          notesApi.get(`${API}/notes/get`, {
            params: {
              exam_type: state.examType,
              subject: state.subject,
              topic: state.topic,
              language: currentLang,
              user_id: user.id,
              _t: Date.now()
            },
            timeout: 30000
          }).then(res => {
            if (res.data.status === "ready") {
              setNotes(res.data.notes);
              setImages(res.data.images || []);
              setNoteId(res.data.note_id || "");
              setGenerationStatus("ready");
              setStep(6);
              setLoading(false);
            } else if (res.data.status === "generating") {
              setGenerationStatus("generating");
              startPolling(state.examType, state.subject, state.topic, currentLang);
            }
          }).catch(() => {
            // If error, just show the selection screen
            localStorage.removeItem('notes_state');
            setStep(1);
            setLoading(false);
          });
        }
      } catch (e) {
        localStorage.removeItem('notes_state');
      }
    }
  }, [user.id, subjectSelection]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Poll for note status when generating
  const startPolling = (examT, subj, top, lang) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    
    pollingRef.current = setInterval(async () => {
      try {
        const response = await notesApi.get(`${API}/notes/status`, {
          params: {
            exam_type: examT,
            subject: subj,
            topic: top,
            language: lang,
            user_id: user.id,
            _t: Date.now() // Cache buster
          }
        });
        
        if (response.data.status === "ready" && response.data.notes) {
          // Notes are ready! Stop polling and display immediately
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          
          // Update all states together
          setNotes(response.data.notes);
          setImages(response.data.images || []);
          setNoteId(response.data.note_id || "");
          setGenerationStatus("ready");
          setPartialNotes(null);
          setPartsCompleted(0);
          setLoading(false);
          setStep(6);
          
          toast.success("नोट्स तैयार हैं! / Notes ready!", { duration: 3000 });
        } else if (response.data.status === "generating") {
          // Update partial content if available
          if (response.data.partial_notes) {
            setPartialNotes(response.data.partial_notes);
          }
          if (response.data.parts_completed !== undefined) {
            setPartsCompleted(response.data.parts_completed);
          }
        }
      } catch (error) {
        console.log("Polling... will retry");
      }
    }, 1500); // Poll every 1.5 seconds for faster progressive updates
  };

  const resetFlow = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    // Clear saved state when user resets
    localStorage.removeItem('notes_state');
    setStep(1);
    setExamType("");
    setSubject("");
    setTopic("");
    setLanguage("");
    setNotes(null); setImages([]); setNoteId("");
    setPartialNotes(null);
    setPartsCompleted(0);
    setGenerationStatus(null);
    setLoading(false);
  };

  // Rewrite notes function - delete old and regenerate
  const handleRewriteNotes = async () => {
    if (rewriteCode !== REWRITE_SECRET_CODE) {
      toast.error("Invalid code! गलत कोड!");
      return;
    }
    
    setRewriteLoading(true);
    setShowRewriteModal(false);
    setRewriteCode("");
    
    try {
      // Step 1: Delete existing notes from cache
      toast.info("Deleting old notes... पुराने नोट्स हटा रहे हैं...");
      await notesApi.delete(`${API}/notes/cache`, {
        params: {
          exam_type: examType,
          subject: subject,
          topic: topic,
          language: language
        }
      });
      
      // Step 2: Reset current notes state
      setNotes(null);
      setImages([]);
      setNoteId("");
      setPartialNotes(null);
      setPartsCompleted(0);
      
      // Step 3: Regenerate fresh notes using /notes/get endpoint
      toast.info("Generating fresh notes... नए नोट्स बना रहे हैं...");
      setLoading(true);
      setRewriteLoading(true);
      setLoadingMessage("Creating fresh notes...");
      setStep(5); // Go to loading state
      
      // Use GET endpoint which triggers fresh generation
      const response = await notesApi.get(`${API}/notes/get`, {
        params: {
          exam_type: examType,
          subject: subject,
          topic: topic,
          language: language,
          user_id: user.id
        }
      });
      
      if (response.data.status === "generating") {
        // Notes are being generated in background
        toast.info("Notes are being regenerated... Please wait or check email.");
        setStep(5);
        // Start polling for completion
        pollForNotes();
      } else if (response.data.notes) {
        // Notes generated immediately
        setNotes(response.data.notes);
        setImages(response.data.images || []);
        setNoteId(response.data.note_id || "");
        setStep(6);
        setLoading(false);
        setRewriteLoading(false);
        toast.success("Notes rewritten successfully! नोट्स फिर से बन गए!");
      }
    } catch (error) {
      console.error("Rewrite error:", error);
      toast.error("Rewrite failed. Please try again.");
      setRewriteLoading(false);
      setLoading(false);
    }
  };
  
  // Poll for notes completion after rewrite
  const pollForNotes = async () => {
    let attempts = 0;
    const maxAttempts = 30; // 30 * 5 seconds = 2.5 minutes max
    
    const poll = setInterval(async () => {
      attempts++;
      try {
        const response = await notesApi.get(`${API}/notes/get`, {
          params: {
            exam_type: examType,
            subject: subject,
            topic: topic,
            language: language,
            user_id: user.id
          }
        });
        
        if (response.data.status === "ready" && response.data.notes) {
          clearInterval(poll);
          setNotes(response.data.notes);
          setImages(response.data.images || []);
          setNoteId(response.data.note_id || "");
          setStep(6);
          setLoading(false);
          setRewriteLoading(false);
          toast.success("Notes ready! नोट्स तैयार हैं!");
        } else if (response.data.status === "failed") {
          clearInterval(poll);
          setLoading(false);
          setRewriteLoading(false);
          toast.error("Note generation failed. Please try again.");
        } else if (attempts >= maxAttempts) {
          clearInterval(poll);
          setLoading(false);
          setRewriteLoading(false);
          toast.info("Taking longer than expected. Check your email for the notes.");
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 5000); // Poll every 5 seconds
  };

  // Check if subject is a language subject (Hindi/English)
  const isLanguageSubject = (subj) => {
    return subj === "hindi" || subj === "english";
  };

  // Handle topic selection - skip language step if medium is already set in subjectSelection
  const handleTopicSelect = (topicName, idx) => {
    const isFree = idx === 0;
    const isFreeUser = !user.plan || user.plan === 'free';
    const isLocked = !isFree && isFreeUser;
    if (isLocked) {
      setShowPremiumPopup(true);
      return;
    }

    // Mark topic as covered in Notes section
    if (user?.id) {
      axios.post(`${API}/progress/mark`, {
        user_id: user.id, exam_type: examType, subject, topic: topicName, section: "notes"
      }).then(() => setCoveredTopics(prev => new Set([...prev, topicName]))).catch(() => {});
    }

    setTopic(topicName);
    
    // Use medium from subjectSelection if available → skip step 4
    const savedLang = subjectSelection?.language;
    if (savedLang) {
      setLanguage(savedLang);
      generateNotesWithLang(savedLang, topicName);
    } else if (isLanguageSubject(subject)) {
      // For language subjects, set language automatically and skip step 4
      const autoLang = subject === "hindi" ? "hindi" : "english";
      setLanguage(autoLang);
      generateNotesWithLang(autoLang, topicName);
    } else {
      // Fallback: show language selection for users without saved medium
      setStep(4);
    }
  };

  // Handle language selection
  const handleLanguageSelect = (selectedLang) => {
    setLanguage(selectedLang);
    generateNotesWithLang(selectedLang, topic);
  };

  // Generate notes with explicit language parameter
  const generateNotesWithLang = async (lang, topicName) => {
    setLoading(true);
    setGenerationStatus("checking");
    setStep(5);

    try {
      const response = await notesApi.get(`${API}/notes/get`, {
        params: {
          exam_type: examType,
          subject: subject,
          topic: topicName,
          language: lang,
          user_id: user.id,
          _t: Date.now()
        },
        timeout: 30000
      });

      if (response.data.status === "ready") {
        setNotes(response.data.notes);
        setImages(response.data.images || []);
        setNoteId(response.data.note_id || "");
        setGenerationStatus("ready");
        setStep(6);
        setLoading(false);
        if (response.data.from_cache) {
          toast.success("नोट्स लोड हो गए! / Notes loaded!");
        }
      } else if (response.data.status === "generating") {
        setGenerationStatus("generating");
        startPolling(examType, subject, topicName, lang);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setShowPremiumPopup(true);
        setStep(3);
        setLoading(false);
      } else {
        setGenerationStatus("generating");
        startPolling(examType, subject, topicName, lang);
      }
    }
  };

  const switchTopic = (newTopic, idx) => {
    const isFree = idx === 0;
    const isFreeUser = !user.plan || user.plan === 'free';
    const isLocked = !isFree && isFreeUser;
    if (isLocked) {
      setShowPremiumPopup(true);
      return;
    }
    
    // Stop any existing polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    
    // For language subjects, always use the correct language; otherwise use saved medium
    const effectiveLang = isLanguageSubject(subject) 
      ? (subject === "hindi" ? "hindi" : "english")
      : (subjectSelection?.language || language);
    
    setTopic(newTopic);
    setLanguage(effectiveLang); // Ensure language state is correct
    setNotes(null); setImages([]); setNoteId("");
    setLoading(true);
    setGenerationStatus("checking");
    setStep(5);
    
    notesApi.get(`${API}/notes/get`, {
      params: { 
        exam_type: examType, 
        subject, 
        topic: newTopic, 
        language: effectiveLang, // Use effective language, not state
        user_id: user.id,
        _t: Date.now() // Cache buster
      },
      timeout: 30000
    }).then(res => {
      if (res.data.status === "ready") {
        setNotes(res.data.notes);
        setImages(res.data.images || []);
        setNoteId(res.data.note_id || "");
        setGenerationStatus("ready");
        setStep(6);
        setLoading(false);
      } else if (res.data.status === "generating") {
        setGenerationStatus("generating");
        startPolling(examType, subject, newTopic, effectiveLang);
      }
    }).catch(err => {
      if (err.response?.status === 403) {
        toast.error("This topic is locked!");
        setStep(6);
        setTopic(topic); // revert
        setLoading(false);
      } else {
        // Don't show error - start polling
        setGenerationStatus("generating");
        startPolling(examType, subject, newTopic, effectiveLang);
      }
    });
  };

  const getSubjectLabel = () => lmsSubjects.find(s => s.id === subject)?.name || subject;
  const getExamLabel = () => lmsExams.find(e => e.id === examType)?.name || examType?.toUpperCase() || "";

  // Handle manual cache clear
  const handleClearCache = async () => {
    toast.loading("Clearing cache...");
    await clearAllCaches();
    toast.dismiss();
    toast.success("Cache cleared! Refreshing...");
    setTimeout(() => window.location.reload(true), 500);
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <nav className="bg-slate-900 shadow-lg sticky top-0 z-50 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Lokesh Sir Notes" className="w-10 h-10 rounded-lg" />
              <span className="text-xl font-bold text-white">
                Lokesh Sir Notes
              </span>
              <Badge className="bg-blue-500/20 text-blue-300 text-xs">v{NOTES_VERSION}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-slate-400 hover:text-white hover:bg-slate-800"
                onClick={handleClearCache}
                title="Clear cache & refresh"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Link to="/dashboard">
                <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                  <Home className="w-4 h-4 mr-2" />Dashboard
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* LMS Preview Banner */}
        {isLmsPreview && (
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-3 rounded-lg mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              <span className="font-medium">LMS Preview Mode - यह वही view है जो students को दिखता है</span>
            </div>
            <Button 
              size="sm" 
              variant="outline" 
              className="bg-white/20 border-white/40 text-white hover:bg-white/30"
              onClick={() => window.close()}
            >
              Close Preview
            </Button>
          </div>
        )}
        
        {/* Breadcrumb */}
        {step > 1 && step < 6 && !isLmsPreview && (
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-6">
            <button onClick={resetFlow} className="hover:text-slate-200">Home</button>
            {examType && <><ChevronRight className="w-3 h-3" /><button onClick={() => { setStep(2); setSubject(""); setTopic(""); setLanguage(""); }} className="hover:text-slate-200">{getExamLabel()}</button></>}
            {subject && <><ChevronRight className="w-3 h-3" /><button onClick={() => { setStep(3); setTopic(""); setLanguage(""); }} className="hover:text-slate-200">{getSubjectLabel()}</button></>}
            {topic && <><ChevronRight className="w-3 h-3" /><span className="text-slate-200 font-medium truncate max-w-[200px]">{topic}</span></>}
          </div>
        )}

        {/* Step 1: Select Exam Type */}
        {step === 1 && (
          <div className="text-center">
            <div className="mb-8">
              <img src={LOGO_URL} alt="Lokesh Sir Notes" className="w-24 h-24 mx-auto mb-4 rounded-2xl shadow-lg" />
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Lokesh Sir Notes</h1>
              <p className="text-slate-300 text-lg">Detailed CBSE Based Expert's Notes</p>
              <p className="text-slate-400 text-sm mt-1">Powered by Lokesh Sir Notes</p>
            </div>
            
            {/* Active Generations Banner - Shows when notes are being generated */}
            {activeGenerations.length > 0 && (
              <Card className="max-w-2xl mx-auto mb-6 border-2 border-blue-700 bg-slate-900" data-testid="active-generations-card">
                <CardHeader className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
                    <CardTitle className="text-lg text-blue-300">
                      Notes बन रहे हैं | Notes Being Generated ({activeGenerations.length})
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="py-2 px-4">
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {activeGenerations.map((job, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-slate-800 rounded-lg p-3 border border-slate-700">
                        <div className="flex-1">
                          <p className="font-medium text-white text-sm">{job.topic}</p>
                          <p className="text-xs text-slate-400">
                            {job.exam_type?.toUpperCase()} | {job.subject?.replace(/_/g, ' ')} | {job.language === 'hindi' ? 'हिंदी' : 'English'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-blue-900/50 text-blue-300 text-xs">
                            {job.phase || 'Generating...'}
                          </Badge>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="h-7 text-xs border-blue-700 text-blue-300 hover:bg-blue-900/30"
                            onClick={() => {
                              setExamType(job.exam_type);
                              setSubject(job.subject);
                              setTopic(job.topic);
                              setLanguage(job.language);
                              setGenerationStatus("generating");
                              setStep(5);
                              startPolling(job.exam_type, job.subject, job.topic, job.language);
                            }}
                          >
                            View
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-blue-400 mt-3 text-center">
                    📧 आपको PDF Email पर मिल जाएगी | You'll receive PDF on email
                  </p>
                </CardContent>
              </Card>
            )}
            
            <div className="grid md:grid-cols-3 gap-4 max-w-2xl mx-auto">
              {loadingExams ? (
                <div className="col-span-3 py-8 text-center text-slate-400">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Loading exams...
                </div>
              ) : (
                lmsExams.map((exam) => (
                  <Card
                    key={exam.id}
                    className="cursor-pointer bg-slate-900 border-slate-700 hover:shadow-xl hover:shadow-blue-500/20 transition-all hover:scale-105 hover:border-blue-500"
                    style={{
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.5)';
                      e.currentTarget.style.outline = '2px solid rgb(59, 130, 246)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '';
                      e.currentTarget.style.outline = '';
                    }}
                    onClick={() => { setExamType(exam.id); setStep(2); }}
                    data-testid={`exam-${exam.id}`}
                  >
                    <CardContent className="py-8 text-center">
                      <Badge className="mb-3 bg-blue-900/50 text-blue-300">{exam.name}</Badge>
                      <h3 className="font-bold text-white mb-1">{exam.name}</h3>
                      <p className="text-sm text-slate-400">{exam.subjects_count} Subject{exam.subjects_count !== 1 ? 's' : ''}</p>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* Step 2: Select Subject */}
        {step === 2 && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              {!isAllSubjects && (
                <Button variant="outline" size="sm" onClick={() => { setStep(1); setExamType(""); }} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              )}
              <div>
                <h2 className="text-2xl font-bold text-white">Select Subject</h2>
                {isAllSubjects && (
                  <p className="text-sm text-green-400 font-medium mt-1">
                    All subjects available — browse freely
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {loadingSubjects ? (
                <div className="col-span-3 py-8 text-center text-slate-400">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Loading subjects...
                </div>
              ) : lmsSubjects.length === 0 ? (
                <div className="col-span-3 py-8 text-center text-slate-400">No subjects found for this exam.</div>
              ) : (
              lmsSubjects.map((subj) => {
                return (
                  <Card
                    key={subj.id}
                    className="cursor-pointer bg-slate-900 border-slate-700 transition-all hover:shadow-lg hover:shadow-blue-500/20 hover:scale-105 hover:border-blue-500"
                    style={{
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.4)';
                      e.currentTarget.style.outline = '2px solid rgb(59, 130, 246)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '';
                      e.currentTarget.style.outline = '';
                    }}
                    onClick={() => {
                      setSubject(subj.id);
                      setStep(3);
                    }}
                    data-testid={`subject-${subj.id}`}
                  >
                    <CardContent className="py-6 text-center relative">
                      <BookOpen className="w-6 h-6 mx-auto mb-2 text-blue-400" />
                      <h3 className="font-semibold text-white">{subj.name}</h3>
                    </CardContent>
                  </Card>
                );
              })
              )}
            </div>
          </div>
        )}

        {/* Step 3: Select Topic */}
        {step === 3 && (
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setSubject(""); setStep(2); setTopic(""); setLanguage(""); }}
                data-testid="back-to-subjects-btn"
                className={isAllSubjects ? "border-blue-600 text-blue-400 hover:bg-blue-900/30" : "border-slate-700 text-slate-300 hover:bg-slate-800"}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {isAllSubjects ? "Browse Other Subjects" : "Back to Subjects"}
              </Button>
              <h2 className="text-2xl font-bold text-white">Select Topic</h2>
            </div>
            <p className="text-slate-400 mb-6">{getExamLabel()} — {getSubjectLabel()}</p>
            {loadingTopics ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-400 mr-2" />
                <span className="text-slate-300">Loading topics...</span>
              </div>
            ) : currentTopics.length === 0 ? (
              <div className="text-center py-8">
                <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p className="text-slate-400">No topics available for this subject.</p>
                <p className="text-slate-500 text-sm mt-2">Please contact admin to add topics.</p>
              </div>
            ) : (
            <div className="space-y-2">
              {currentTopics.map((t, idx) => {
                const isFree = idx === 0;
                const isFreeUser = !user.plan || user.plan === 'free';
                const isLocked = !isFree && isFreeUser;
                const isCovered = coveredTopics.has(t.value);
                return (
                  <Card
                    key={t.value}
                    className={`cursor-pointer bg-slate-900 border transition-all ${
                      isLocked ? 'opacity-60 border-slate-700' : 'border-slate-700 hover:shadow-md hover:border-blue-500'
                    } ${isCovered ? 'ring-2 ring-green-500' : ''}`}
                    style={!isLocked ? {
                      transition: 'all 0.3s ease'
                    } : {}}
                    onMouseEnter={(e) => {
                      if (!isLocked) {
                        if (isCovered) {
                          e.currentTarget.style.boxShadow = '0 0 20px rgba(34, 197, 94, 0.5)';
                        } else {
                          e.currentTarget.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.4)';
                        }
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '';
                    }}
                    onClick={() => handleTopicSelect(t.value, idx)}
                    data-testid={`topic-${idx}`}
                  >
                    <CardContent className="py-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-mono text-slate-500 w-8">{String(idx + 1).padStart(2, '0')}</span>
                        <span className="font-medium text-white">{t.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {isCovered && <span className="text-[10px] font-bold text-green-400 bg-green-900/50 border border-green-700 px-2 py-0.5 rounded-full">Covered</span>}
                        {isFree && !isCovered && <Badge className="bg-green-900/50 text-green-300 border-green-700">Free</Badge>}
                        {isLocked ? <Lock className="w-4 h-4 text-slate-600" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            )}
            {/* Paywall for locked topics */}
            {!loadingTopics && (!user.plan || user.plan === 'free') && currentTopics.length > 1 && (
              <Card className="mt-6 border-2 border-purple-700 bg-gradient-to-br from-purple-900/30 to-indigo-900/30">
                <CardContent className="py-6 text-center">
                  <Lock className="w-8 h-8 mx-auto mb-3 text-purple-400" />
                  <p className="text-slate-300 mb-1">First topic is FREE! Unlock all topics with</p>
                  <p className="font-bold text-purple-400 mb-4">Premium Plan @ ₹499</p>
                  <Button 
                    onClick={() => setShowPremiumPopup(true)}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
                    data-testid="unlock-notes-btn"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />Unlock Now
                  </Button>
                </CardContent>
              </Card>
            )}
            <Button variant="outline" className="mt-6 border-slate-700 text-slate-300 hover:bg-slate-800" onClick={() => { setStep(2); setSubject(""); }}>
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
          </div>
        )}

        {/* Step 4: Select Language - Skip for language subjects */}
        {step === 4 && (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-2">Select Language / भाषा चुनें</h2>
            <p className="text-slate-400 mb-8">Choose your preferred language for notes</p>
            <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
              {[
                { value: "english", label: "English", icon: "A", accent: "blue" },
                { value: "hindi", label: "हिंदी", icon: "अ", accent: "orange" }
              ].map((lang) => (
                <Card
                  key={lang.value}
                  className={`cursor-pointer bg-slate-900 border-slate-700 hover:shadow-xl transition-all hover:scale-105 hover:border-${lang.accent}-500`}
                  style={{ transition: 'all 0.3s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = `0 0 20px rgba(${lang.accent === 'blue' ? '59, 130, 246' : '249, 115, 22'}, 0.5)`;
                  }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; }}
                  onClick={() => handleLanguageSelect(lang.value)}
                  data-testid={`lang-${lang.value}`}
                >
                  <CardContent className="py-8 text-center">
                    <div className={`w-16 h-16 rounded-full bg-${lang.accent}-500/20 flex items-center justify-center mx-auto mb-3 text-3xl font-bold text-${lang.accent}-400`}>
                      {lang.icon}
                    </div>
                    <h3 className="font-bold text-white text-lg">{lang.label}</h3>
                  </CardContent>
                </Card>
              ))}
            </div>
            <Button variant="outline" className="mt-6 border-slate-700 text-slate-300 hover:bg-slate-800" onClick={() => { setStep(3); setTopic(""); }}>
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
          </div>
        )}

        {/* Step 5: Loading with Countdown Timer */}
        {step === 5 && (loading || generationStatus === "generating") && (
          <NotesLoadingScreen 
            topic={topic} 
            subject={subject} 
            isGenerating={generationStatus === "generating"} 
            partialNotes={partialNotes}
            partsCompleted={partsCompleted}
            language={language}
          />
        )}

        {/* Step 6: Display Notes with Sidebar */}
        {step === 6 && notes && (
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar - Topics (desktop: left, mobile: after notes) */}
            <aside className="order-2 lg:order-1 lg:w-72 lg:flex-shrink-0">
              <div className="lg:sticky lg:top-20">
                <Card className="border border-gray-200">
                  <CardHeader className="py-3 px-4 bg-gray-50 border-b">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{getExamLabel()} - {getSubjectLabel()}</p>
                  </CardHeader>
                  <CardContent className="p-0 max-h-[60vh] lg:max-h-[70vh] overflow-y-auto">
                    {loadingTopics ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                      </div>
                    ) : currentTopics.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-4">No topics</p>
                    ) : (
                    currentTopics.map((t, idx) => {
                      const isFree = idx === 0;
                      const isFreeUser = !user.plan || user.plan === 'free';
                      const isLocked = !isFree && isFreeUser;
                      const isActive = t.value === topic;
                      return (
                        <button
                          key={t.value}
                          onClick={() => !isActive && switchTopic(t.value, idx)}
                          className={`w-full text-left px-4 py-3 text-sm border-b border-gray-100 flex items-center gap-2 transition-colors ${
                            isActive ? 'bg-amber-50 text-amber-800 font-semibold border-l-4 border-l-amber-500' :
                            isLocked ? 'text-gray-400 cursor-not-allowed' :
                            'text-gray-700 hover:bg-gray-50 cursor-pointer'
                          }`}
                          disabled={isActive}
                          data-testid={`sidebar-topic-${idx}`}
                        >
                          <span className="text-xs text-gray-400 w-5 flex-shrink-0">{idx + 1}</span>
                          <span className="flex-1 truncate">{t.label}</span>
                          {isFree && !isActive && <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0">Free</Badge>}
                          {isLocked && <Lock className="w-3 h-3 text-gray-300 flex-shrink-0" />}
                        </button>
                      );
                    })
                    )}
                  </CardContent>
                </Card>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setStep(4); setLanguage(""); setNotes(null); setImages([]); setNoteId(""); }}>
                    <Globe className="w-3 h-3 mr-1" />{language === "hindi" ? "Switch to English" : "Switch to Hindi"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={resetFlow}>
                    <Home className="w-3 h-3 mr-1" />Home
                  </Button>
                </div>
              </div>
            </aside>

            {/* Main content - Notes */}
            <div className="order-1 lg:order-2 flex-1 min-w-0">
              {/* Notes Header */}
              <div className="bg-gray-900 rounded-t-2xl p-6 text-center relative">
                {/* Rewrite Button - Small icon in corner */}
                <button
                  onClick={() => setShowRewriteModal(true)}
                  className="absolute top-3 right-3 p-2 rounded-full bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                  title="Rewrite Notes"
                  data-testid="rewrite-notes-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                
                <div className="flex items-center justify-center gap-3 mb-3">
                  <img src={LOGO_URL} alt="Lokesh Sir Notes" className="w-12 h-12 rounded-lg" />
                  <div className="text-left">
                    <h2 className="text-white font-bold text-lg">Lokesh Sir Notes</h2>
                    <p className="text-gray-400 text-xs">Quality at its peak</p>
                  </div>
                </div>
                <h1 className="text-xl md:text-2xl font-bold text-white mb-1">{topic}</h1>
                <div className="flex items-center justify-center gap-3 text-sm text-gray-400">
                  <Badge className="bg-amber-500/20 text-amber-300">{getExamLabel()}</Badge>
                  <span>{getSubjectLabel()}</span>
                  <Badge variant="outline" className="border-gray-600 text-gray-400">
                    <Globe className="w-3 h-3 mr-1" />{language === "hindi" ? "Hindi" : "English"}
                  </Badge>
                </div>
              </div>

              {/* Notes Content */}
              <div className="bg-white rounded-b-2xl shadow-xl p-6 md:p-10 border border-t-0 border-gray-200">
                <NotesRenderer content={notes} images={images} noteId={noteId} topic={topic} subject={subject} examType={examType} language={language} />

                {/* Action Buttons: Practice Tests */}
                <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center items-center">
                  <Link to={`/evaluate?exam_type=${examType}&subject=${subject}&topic=${encodeURIComponent(topic)}`} data-testid="notes-to-descriptive">
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl shadow-lg">
                      <Upload className="w-5 h-5 mr-2" />Descriptive Test
                    </Button>
                  </Link>
                  <Link to={`/mcq-practice?exam_type=${examType}&subject=${subject}&topic=${encodeURIComponent(topic)}`} data-testid="notes-to-objective">
                    <Button className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl shadow-lg">
                      <Target className="w-5 h-5 mr-2" />Objective Test
                    </Button>
                  </Link>
                </div>

                {/* Footer Branding */}
                <div className="mt-12 pt-6 border-t-2 border-gray-200 text-center">
                  <img src={LOGO_URL} alt="Lokesh Sir Notes" className="w-10 h-10 mx-auto mb-2 rounded-lg opacity-60" />
                  <p className="text-sm text-gray-400">Prepared by Lokesh Sir Notes</p>
                  <p className="text-xs text-gray-300">Quality at its peak</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      
      {/* Floating Back Button */}
      <FloatingBackButton 
        onBack={
          step === 6 ? () => { setStep(3); setTopic(""); setLanguage(""); setNotes(null); setImages([]); setNoteId(""); } :
          step === 5 ? () => { setStep(3); setTopic(""); setLanguage(""); setNotes(null); setImages([]); setNoteId(""); } :
          step === 4 ? () => { setStep(3); setLanguage(""); } :
          step === 3 ? () => { setStep(2); setSubject(""); } :
          step === 2 ? () => { setStep(1); setExamType(""); } :
          null
        }
        showHome={true}
      />
      
      {/* Premium Popup for locked content */}
      {showPremiumPopup && (
        <PremiumPopup
          user={user}
          onClose={() => setShowPremiumPopup(false)}
          onUpgrade={onLogin}
        />
      )}
      
      {/* Rewrite Code Modal */}
      {showRewriteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="text-center mb-4">
              <RefreshCw className="w-12 h-12 mx-auto text-blue-600 mb-3" />
              <h3 className="text-lg font-bold text-gray-900">Rewrite Notes</h3>
              <p className="text-sm text-gray-500 mt-1">Enter admin code to regenerate</p>
            </div>
            
            <input
              type="password"
              value={rewriteCode}
              onChange={(e) => setRewriteCode(e.target.value)}
              placeholder="Enter Code"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-center text-lg font-mono tracking-widest focus:border-blue-500 focus:outline-none"
              maxLength={4}
              autoFocus
              data-testid="rewrite-code-input"
            />
            
            <div className="flex gap-3 mt-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowRewriteModal(false); setRewriteCode(""); }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={handleRewriteNotes}
                disabled={rewriteCode.length !== 4 || rewriteLoading}
                data-testid="confirm-rewrite-btn"
              >
                {rewriteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Rewrite"}
              </Button>
            </div>
            
            <p className="text-xs text-gray-400 text-center mt-3">
              This will delete existing notes and create fresh ones
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
